import React, { useContext, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Button from '@mui/material/Button';
import Box from "@mui/material/Box";
import Grid from '@mui/material/Grid';
import TextField from "@mui/material/TextField";
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import AddCircle from '@mui/icons-material/AddCircle';
import Cancel from '@mui/icons-material/Cancel';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { NotificationContainer, NotificationManager } from 'react-notifications';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DateTime } from "luxon";
import './jdupload.css';
import { useLocation } from 'react-router-dom';
import { UserTypeContext } from "../../AppRoute";
import jobDescription from '../../apis/candidatescreening/jobdescription';
import InfoIcon from '../Icons/infoicon';
import Switch from '@mui/material/Switch';
import { Autocomplete, InputAdornment } from '@mui/material';
import getAllPrimarySkillData from '../../apis/getAllPrimarySkillData';
import { BlobServiceClient } from '@azure/storage-blob';
import jsonConfig from '../../Config.json';

const JD_BLOB_CONTAINER = 'screeningjd';
const MAX_SKILLS = 10;
const MAX_TOTAL_WEIGHTAGE = 100;

const getTotalWeightage = (skillsList, excludeIndex = -1) =>
  skillsList.reduce((sum, skill, i) => {
    if (i === excludeIndex || skill.weightage === '' || skill.weightage === undefined) {
      return sum;
    }
    return sum + Number(skill.weightage);
  }, 0);

function create_UUID() {
  let dt = new Date().getTime();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const uploadJobDescToBlob = async (jobDescriptionText) => {
  const uuid = create_UUID();
  const extension = 'txt';
  const blobFileName = `${uuid}.${extension}`;
  const sas = jsonConfig.sasToken;
  const bsClient = new BlobServiceClient(`${jsonConfig.ctResourcesCdn + sas}`);
  const containerClient = bsClient.getContainerClient(JD_BLOB_CONTAINER);
  const blobClient = containerClient.getBlockBlobClient(blobFileName);
  const blob = new Blob([jobDescriptionText], { type: 'text/plain' });

  await blobClient.uploadBrowserData(blob, {
    blobHTTPHeaders: { blobContentType: 'text/plain' },
  });

  return { fName: uuid, fext: extension };
};

const JDUpload = () => {
  const [jobDesc, setJobDescription] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [expiryAt, setExpiryAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [showAIParameters, setShowAIParameters] = useState(true);
  const [critical, setCritical] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState();
  const [skillOptions, setSkillOptions] = useState([]);

  const [skills, setSkills] = useState([
    {
      skill: '',
      weightage: '',
      difficultyLevel: '',
      isPrimary: false,
    }
  ]);
  const skillDifficultyOptions = ['Basic', 'Intermediate', 'Advanced'];

  const location = useLocation();
  const { userType, userRecords } = useContext(UserTypeContext);
  const userRecruitmentpartnerId = userType === "Admin" ? location?.state?.recruitmentPartnerId : userRecords?.recruitmentpartnerId;
  const idContextTest = userType === "Admin" ? location?.state?.id : userRecords?.id;
  const userRole = userType === "Admin" ? location?.state?.userRole : userRecords?.role?.roleName;
  const userRecordData = userType === "Admin" ? location?.state?.userRecords : userRecords;

  useEffect(() => {
    const fetchPrimarySkills = async () => {
      try {
        const payload = {
          userRecruitmentpartnerId: userRecruitmentpartnerId,
          userId: idContextTest,
        }
        const response = await getAllPrimarySkillData(payload);
        const options = response.data.data.map(skill => skill.name);
        setSkillOptions(options);
      } catch (error) {
        console.error('Error fetching primary skills:', error);
      }
    };
    fetchPrimarySkills();
  }, []);

  const addSkill = () => {
    if (skills.length >= MAX_SKILLS) {
      NotificationManager.warning(
        `Maximum ${MAX_SKILLS} skills allowed`,
        'Warning',
        5000
      );
      return;
    }
    setSkills([
      ...skills,
      {
        skill: '',
        weightage: '',
        difficultyLevel: '',
        isPrimary: false,
      }
    ]);
  };

  const removeSkill = (index) => {
    if (skills.length > 1) {
      const updatedSkills = [...skills];
      const wasPrimary = updatedSkills[index].isPrimary;
      updatedSkills.splice(index, 1);
      if (wasPrimary && updatedSkills.length > 0) {
        updatedSkills[0].isPrimary = true;
      }
      setSkills(updatedSkills);
    }
  };

  const handleSkillChange = (value, index, field) => {
    const updatedSkills = [...skills];
    updatedSkills[index][field] = value;
    setSkills(updatedSkills);
  };

  const totalWeightage = getTotalWeightage(skills);

  const handleWeightageChange = (value, index) => {
    if (value === '') {
      handleSkillChange('', index, 'weightage');
      return;
    }

    const numVal = Number(value);
    if (Number.isNaN(numVal) || numVal < 0) return;

    const otherTotal = getTotalWeightage(skills, index);
    const maxAllowed = MAX_TOTAL_WEIGHTAGE - otherTotal;

    if (maxAllowed <= 0) {
      handleSkillChange(0, index, 'weightage');
      return;
    }

    handleSkillChange(Math.min(numVal, MAX_TOTAL_WEIGHTAGE, maxAllowed), index, 'weightage');
  };

  const handlePrimaryToggle = (index, checked) => {
    if (checked) {
      setSkills(skills.map((skill, i) => ({
        ...skill,
        isPrimary: i === index,
      })));
    } else {
      setSkills(skills.map((skill, i) => ({
        ...skill,
        isPrimary: i === index ? false : skill.isPrimary,
      })));
    }
  };

  const handleJobDescriptionChange = (e) => {
    setJobDescription(e.target.value);
  };

  const handleJobTitleChange = (e) => {
    setJobTitle(e.target.value);
  };

  const handleClear = () => {
    setJobDescription('');
    setJobTitle('');
    setExpiryAt(null);
    setGeneratedLink('');
    setCopied(false);
    setSelectedDuration('');
    setCritical('');
    setSkills([
      {
        skill: '',
        weightage: '',
        difficultyLevel: '',
        isPrimary: true,
      }
    ]);
  };

  const handleCancelLink = () => {
    setGeneratedLink('');
    setCopied(false);
    setJobDescription('');
    setJobTitle('');
    setExpiryAt(null);
    setSelectedDuration('');
    setCritical('');
    setSkills([
      {
        skill: '',
        weightage: '',
        difficultyLevel: '',
        isPrimary: true,
      }
    ]);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!jobTitle.trim()) {
      NotificationManager.error('Please enter a job title', 'Error', 5000);
      return;
    }

    if (!jobDesc.trim()) {
      NotificationManager.error('Please enter a job description', 'Error', 5000);
      return;
    }

    if (jobDesc.trim().length < 50) {
      NotificationManager.error('Job description must be at least 50 characters long', 'Error', 5000);
      return;
    }

    if (!expiryAt) {
      NotificationManager.error('Please select an expiry date', 'Error', 5000);
      return;
    }
    if (!selectedDuration || selectedDuration < 300 || selectedDuration > 600) {
      NotificationManager.error('Please enter a duration between 300-600 seconds', 'Error', 5000);
      return;
    }

    for (let i = 0; i < skills.length; i++) {
      const skill = skills[i];
      if (!skill.skill.trim()) {
        NotificationManager.error(`Please enter skill name for row ${i + 1}`, 'Error', 5000);
        return;
      }
      if (!skill.difficultyLevel) {
        NotificationManager.error(`Please select proficiency level for row ${i + 1}`, 'Error', 5000);
        return;
      }
      if (!skill.weightage && skill.weightage !== 0) {
        NotificationManager.error(`Please enter weightage for row ${i + 1}`, 'Error', 5000);
        return;
      }
      if (Number(skill.weightage) < 0) {
        NotificationManager.error(`Please enter valid weightage for row ${i + 1}`, 'Error', 5000);
        return;
      }
    }

    const primaryCount = skills.filter((skill) => skill.isPrimary).length;
    if (primaryCount !== 1) {
      NotificationManager.error('Please mark exactly one skill as primary', 'Error', 5000);
      return;
    }

    const totalWeightageOnSubmit = getTotalWeightage(skills);
    if (totalWeightageOnSubmit > MAX_TOTAL_WEIGHTAGE) {
      NotificationManager.error(
        `Total weightage across all skills cannot exceed ${MAX_TOTAL_WEIGHTAGE}`,
        'Error',
        5000
      );
      return;
    }

    setLoading(true);
    let payload = {
      createdBy: idContextTest,
      recruitmentpartnerId: userRecruitmentpartnerId,
      jobTitle: jobTitle.trim(),
      jobDesc: jobDesc,
      difficultyLevel: critical,
      type: showAIParameters ? "IT" : "NONIT",
      assessmentDurationInSeconds: selectedDuration,
      isActive: true,
      skills: skills.map((skill) => ({
        skill: skill.skill.trim(),
        weightage: Number(skill.weightage),
        difficultyLevel: skill.difficultyLevel,
        isPrimary: Boolean(skill.isPrimary),
      })),
    }

    const dt = DateTime.fromJSDate(expiryAt);
    payload.expiryAt = dt.toFormat('yyyy-MM-dd');

    try {
      try {
        const { fName, fext } = await uploadJobDescToBlob(jobDesc);
        payload.fName = fName;
        payload.fext = fext;
      } catch (blobError) {
        console.error('Error uploading job description to blob:', blobError);
        NotificationManager.error(
          'Failed to upload job description file. Please try again.',
          'Error',
          5000
        );
        return;
      }

      const response = await jobDescription(payload, false);
      const link = response?.data?.data?.link ? `${response.data.data.link}?int=ai` : '';
      setGeneratedLink(link);
      setCopied(false);
      NotificationManager.success(
        'Job description uploaded successfully',
        'Success',
        5000
      );
    } catch (error) {
      console.error('Error uploading job description:', error);
      NotificationManager.error(
        'An error occurred while uploading. Please try again.',
        'Error',
        5000
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(generatedLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = generatedLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      NotificationManager.success('Link copied to clipboard!', 'Success', 3000);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Copy failed:', error);
      NotificationManager.error('Failed to copy link', 'Error', 3000);
    }
  };

  return (
    <>
      <Grid container spacing={0} justifyContent="center" className="jd-upload-grid-container">
        <Grid item xs={12} sm={12} md={8} className="jd-upload-grid-item">
          <div className="jd-upload-paper">
            <div className="jd-upload-form">
              <div className="jd-link-st-phase">
                <div>
                  <div className='six-column-grid'>
                    <Box className="jd-job-title">
                      <Typography
                        variant="subtitle2"
                        className="jd-upload-title"
                      >
                        Job Title *
                      </Typography>
                      <TextField
                        id="jobTitle"
                        variant="outlined"
                        placeholder="e.g., Senior Software Engineer"
                        value={jobTitle}
                        onChange={handleJobTitleChange}
                        disabled={loading || !!generatedLink}
                        required
                        className="jd-job-title-input"
                        fullWidth
                        size="small"
                      />
                    </Box>

                    <Box className="jd-expiry-date">
                      <Typography
                        variant="subtitle2"
                        className="jd-upload-expiry-title"
                      >
                        Expiry Date*
                      </Typography>
                      <div className="date-picker-filter-container">
                        <DatePicker
                          selected={expiryAt}
                          onChange={(date) => setExpiryAt(date)}
                          dateFormat="MM/dd/yyyy"
                          placeholderText="Select expiry date"
                          showIcon={false}
                          disabled={loading || !!generatedLink}
                          minDate={DateTime.now().plus({ days: 1 }).toJSDate()}
                          required
                          className="date-picker-table-filter"
                        />
                      </div>
                    </Box>

                    <Box className="jd-job-title">
                      <Typography
                        variant="subtitle2"
                        className="jd-upload-title"
                      >
                        Critical Level*
                      </Typography>
                      <Autocomplete
                        options={['Advanced', 'Intermediate', 'Beginner']}
                        value={critical}
                        onChange={(event, newValue) => setCritical(newValue)}
                        disabled={loading || !!generatedLink}
                        fullWidth
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Priority"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                    </Box>

                    <Box className="jd-expiry-date">
                      <Typography
                        variant="subtitle2"
                        className="jd-upload-expiry-title"
                      >
                        Duration (sec)*
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        value={selectedDuration}
                        disabled={loading || !!generatedLink}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedDuration(val === '' ? '' : Math.max(1, Number(val)));
                        }}
                        slotProps={{
                          htmlInput: {
                            min: 1,
                            onKeyDown: (e) => {
                              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                e.preventDefault();
                              }
                            },
                          },
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <span>sec</span>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>
                  </div>
                </div>

                <Grid item xs={12} sm={12} md={4} className="jd-link-panel-item">
                  <div className="jd-link-panel">
                    {!generatedLink ? (
                      <div className="jd-link-locked">
                        <Typography
                          variant="body2"
                          className="jd-locked-description"
                        >
                          Create a job description to unlock the shareable link
                        </Typography>
                      </div>
                    ) : (
                      <div className="jd-link-unlocked">
                        <p className="jd-unlocked-title">
                          Job Description Link
                        </p>

                        <div className="jd-link-display-container">
                          <TextField
                            value={generatedLink}
                            variant="outlined"
                            fullWidth
                            size="small"
                            disabled
                            InputProps={{
                              readOnly: true,
                              className: 'jd-link-display-input',
                            }}
                          />
                          <div className="jd-link-buttons-group">
                            <IconButton
                              onClick={handleCopyLink}
                              className={`jd-copy-icon-btn ${copied ? 'copied' : ''}`}
                              aria-label="copy link"
                              size="small"
                            >
                              {copied ? (
                                <CheckCircleIcon />
                              ) : (
                                <ContentCopyIcon />
                              )}
                            </IconButton>
                            <IconButton
                              onClick={handleCancelLink}
                              className="jd-refresh-icon-btn"
                              aria-label="refresh"
                              size="small"
                            >
                              <RefreshIcon />
                            </IconButton>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Grid>

              <Box className="jd-additional-skills-section" sx={{ mt: 3 }}>
                <Typography variant="h6" className="jd-upload-expiry-title">
                  Skills
                </Typography>
                <Typography variant="body2" className="jd-section-subtitle" sx={{ mb: 2 }}>
                  Add skills required for this position. Mark exactly one as primary. Total weightage: {totalWeightage} / {MAX_TOTAL_WEIGHTAGE}
                </Typography>

                <Table className="jd-additional-skills-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Skill Name*</TableCell>
                      <TableCell>Proficiency Level*</TableCell>
                      <TableCell>Weightage*</TableCell>
                      <TableCell>Primary</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {skills.map((skill, index) => (
                      <TableRow key={index} className="jd-skill-row">
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={skillOptions}
                            value={skill.skill}
                            onChange={(event, newValue) => {
                              handleSkillChange(newValue || '', index, 'skill');
                            }}
                            onInputChange={(event, newInputValue) => {
                              handleSkillChange(newInputValue, index, 'skill');
                            }}
                            disabled={loading || !!generatedLink}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select or type a skill"
                                size="small"
                                fullWidth
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            options={skillDifficultyOptions}
                            value={skill.difficultyLevel}
                            onChange={(event, newValue) => {
                              handleSkillChange(newValue, index, 'difficultyLevel');
                            }}
                            disabled={loading || !!generatedLink}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select level"
                                size="small"
                                fullWidth
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            placeholder="Weightage"
                            value={skill.weightage}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                handleWeightageChange('', index);
                                return;
                              }

                              const numVal = Number(val);
                              if (numVal >= 0 && numVal <= MAX_TOTAL_WEIGHTAGE) {
                                handleWeightageChange(numVal, index);
                              }
                            }}
                            onBlur={(e) => {
                              const val = e.target.value;
                              if (val === '') return;

                              const otherTotal = getTotalWeightage(skills, index);
                              const maxAllowed = MAX_TOTAL_WEIGHTAGE - otherTotal;
                              let numVal = Number(val);
                              numVal = Math.max(0, Math.min(MAX_TOTAL_WEIGHTAGE, maxAllowed, numVal));
                              handleSkillChange(numVal, index, 'weightage');
                            }}
                            disabled={loading || !!generatedLink}
                            slotProps={{
                              htmlInput: {
                                min: 0,
                                max: MAX_TOTAL_WEIGHTAGE,
                                onKeyDown: (e) => {
                                  if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                },
                              },
                            }}
                            error={
                              skill.weightage !== '' &&
                              (Number(skill.weightage) < 0 ||
                            Number(skill.weightage) > MAX_TOTAL_WEIGHTAGE ||
                            totalWeightage > MAX_TOTAL_WEIGHTAGE)
                            }
                            helperText={
                              skill.weightage !== '' &&
                              (Number(skill.weightage) < 0 || Number(skill.weightage) > MAX_TOTAL_WEIGHTAGE)
                                ? `Value must be between 0 and ${MAX_TOTAL_WEIGHTAGE}`
                                : totalWeightage > MAX_TOTAL_WEIGHTAGE
                                  ? `Total weightage cannot exceed ${MAX_TOTAL_WEIGHTAGE}`
                                  : ''
                            }
                          />
                        </TableCell>
                        <TableCell className="jd-skill-primary-cell">
                          <Switch
                            checked={Boolean(skill.isPrimary)}
                            onChange={(e) => handlePrimaryToggle(index, e.target.checked)}
                            disabled={loading || !!generatedLink}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell className="jd-skill-action-cell">
                          <button
                            type="button"
                            className="jd-remove-skill-btn"
                            onClick={() => removeSkill(index)}
                            disabled={skills.length <= 1 || loading || !!generatedLink}
                            aria-label="Remove skill"
                          >
                            <Cancel />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="jd-add-skill-row">
                      <TableCell colSpan={5}>
                        <button
                          type="button"
                          className="jd-add-skill-btn"
                          onClick={addSkill}
                          disabled={loading || !!generatedLink || skills.length >= MAX_SKILLS}
                        >
                          <AddCircle className="jd-add-skill-btn-icon" />
                          <span>Add Skill</span>
                        </button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
              </div>

              <Box className="jd-textarea-wrapper">
                <div className="jd-ai-container">
                  <Typography variant="h4" component="h1" className="jd-upload-title">
                    Upload Job Description
                  </Typography>
                  <div className="jd-ai-capsule">
                    <Switch
                      checked={showAIParameters}
                      onChange={() => setShowAIParameters(!showAIParameters)}
                      color="primary"
                      size="small"
                    />
                    <span className={`jd-ai-status ${showAIParameters ? 'active' : ''}`}>
                      {showAIParameters ? "IT" : "Non-IT"}
                    </span>
                  </div>
                </div>

                <Typography variant="body2" className="jd-upload-subtitle">
                  Copy and paste the complete job description below to get started.
                </Typography>
                <TextareaAutosize
                  id="jobDesc"
                  minRows={12}
                  maxRows={25}
                  placeholder="Paste your job description here..."
                  value={jobDesc}
                  onChange={handleJobDescriptionChange}
                  className="jd-textarea"
                  disabled={loading || !!generatedLink}
                />
                <Typography variant="caption" className="jd-char-count">
                  {jobDesc.length} characters
                </Typography>
              </Box>

              

            </div>
            <Box className="jd-button-group">
              <Button
                type="submit"
                variant="contained"
                className="jd-submit-btn"
                disabled={loading || !jobDesc.trim() || !!generatedLink}
                onClick={handleSubmit}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} className="jd-loading-spinner" />
                    Uploading...
                  </>
                ) : (
                  'Submit Job Description'
                )}
              </Button>

              <Button
                type="button"
                variant="outlined"
                className="jd-clear-btn"
                onClick={handleClear}
                disabled={loading || !!generatedLink}
              >
                Clear
              </Button>
            </Box>

            <Typography variant="caption" className="jd-help-text">
              <InfoIcon /> Please ensure the job description is clear and complete for better results.
            </Typography>

          </div>
        </Grid>
      </Grid>

      <NotificationContainer />
    </>
  );
};

export default JDUpload;